import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import ThemeToggle from '../components/ThemeToggle';
import { createCollection } from '../services/collectionService';
import { getConnectionId, saveActiveCollectionId } from '../utils/storage';

const CreateCollection: React.FC = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [collectionName, setCollectionName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  const handleCreate = async () => {
    const connectionId = getConnectionId();
    if (!connectionId) {
      setToast({ message: 'No database connection selected', type: 'error' });
      return;
    }
    if (!collectionName.trim()) {
      setToast({ message: 'Please enter a collection name', type: 'warning' });
      return;
    }

    setIsCreating(true);
    try {
      const collection = await createCollection({
        connection_id: parseInt(connectionId),
        name: collectionName.trim(),
      });
      saveActiveCollectionId(collection.id, connectionId);
      setToast({ message: 'Collection created successfully', type: 'success' });
      setTimeout(() => navigate('/home', { replace: true }), 300);
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : 'Failed to create collection',
        type: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="absolute top-6 right-6">
        <ThemeToggle />
      </div>

      <button
        onClick={() => setShowModal(true)}
        className="group flex flex-col items-center justify-center w-64 h-64 bg-white/90 dark:bg-slate-800/90 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-2xl shadow-xl hover:shadow-2xl hover:border-blue-500 dark:hover:border-blue-400 transition-all"
      >
        <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 mb-4" style={{ fontSize: '64px' }}>
          create_new_folder
        </span>
        <span className="text-xl font-bold text-slate-900 dark:text-white">Create Collection</span>
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Name Your Collection</h3>
              <input
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="Progress Report"
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white"
              />
              <div className="flex justify-end gap-3 mt-5">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={isCreating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={isCreating}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateCollection;
